import React from 'react';

import classNames from 'classnames';

import cls from './index.module.css';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    classes?: string[];
    label?: string;
    error?: string;
    required?: boolean;
    leftIcon?: React.ReactNode;
    rightIconHandler?: () => void;
    rightIcon?: React.ReactNode;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onValueChange?: (name: string, value: string) => void;
    innerRef?: React.Ref<HTMLInputElement>;
    inputClassName?: string;
    labelClassName?: string;
    inputErrorClassName?: string;
    errorClassName?: string;
    renderInput?: (inputProps: React.InputHTMLAttributes<HTMLInputElement>) => React.ReactNode;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
    (
        {
            classes,
            label,
            error,
            rightIcon,
            leftIcon,
            onChange,
            onValueChange,
            innerRef,
            inputClassName,
            inputErrorClassName,
            errorClassName,
            value,
            name,
            renderInput,
            required = false,
            rightIconHandler,
            labelClassName,
            ...restProps
        },
        ref
    ) => {
        const combinedRef = ref ?? innerRef;

        const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (onChange) {
                onChange(e);
            }
            if (onValueChange) {
                onValueChange(e.target.name, e.target.value);
            }
        };

        const baseInputClass = classNames(
            cls.input,
            inputClassName,
            {
                [cls.inputWithLeftIcon]: leftIcon,
                [cls.inputWithRightIcon]: rightIcon,
                [cls.inputError]: error,
            },
            inputErrorClassName
        );

        return (
            <div className={classNames(cls.inputWrapper, classes)}>
                {label && (
                    <label className={classNames(cls.label, labelClassName)} htmlFor={name}>
                        {label}
                        {required && <span className={cls.required}>*</span>}
                    </label>
                )}
                {renderInput ? (
                    renderInput({
                        ...restProps,
                        className: baseInputClass,
                    })
                ) : (
                    <div className={cls.inputContainer}>
                        {leftIcon && <span className={cls.leftIcon}>{leftIcon}</span>}
                        <input
                            className={baseInputClass}
                            onChange={onChangeHandler}
                            value={value ?? ''}
                            ref={combinedRef}
                            name={name}
                            id={name}
                            {...restProps}
                        />
                        {rightIcon && (
                            <span onClick={rightIconHandler} className={cls.rightIcon}>
                                {rightIcon}
                            </span>
                        )}
                    </div>
                )}
                {error && <div className={classNames(cls.error, errorClassName)}>{error}</div>}
            </div>
        );
    }
);
Input.displayName = 'Input';
